// ProfileScreen (§10) — the "You" tab: avatar, XP bar, stat cards, achievements
// (computed from real state), Share progress, and the Settings entry.
import { AD_SLOTS } from "../../config/adSlots";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Screen from "../ui/Screen";
import { StatCard } from "../ui/primitives";
import { useElementStore } from "../../store/elementStore";
import { levelFromDiscovered, levelProgress, getStreak } from "../../game/stats";
import { getAllAchievements } from "../../services/progressionService";
import { loadUserProfile } from "../../services/storageService";
import { share } from "../../game/share";
import { ELEMENT_DEFS } from "../elementDefinitions";

const TITLES = ["Apprentice", "Novice Alchemist", "Alchemist", "Adept", "Master Alchemist", "Grand Alchemist", "Archmage", "Legend of the Craft"];
const titleFor = (level: number) => TITLES[Math.min(level - 1, TITLES.length - 1)] || "Apprentice";

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const unlocked = useElementStore((s) => s.unlocked);
  const discovered = unlocked.size;
  const total = Object.keys(ELEMENT_DEFS).length;
  const level = levelFromDiscovered(discovered);
  const prog = levelProgress(discovered);
  const streak = getStreak().streak;
  const profile = loadUserProfile();
  const name = profile.displayName || "Alchemist";

  const achievements = useMemo(() => getAllAchievements(), []);
  const earnedCount = achievements.filter((a) => a.unlocked).length;

  const onShare = () => share(`I've discovered ${discovered}/${total} elements in MystiCrafter (Level ${level})! ⚗️`);

  return (
    <Screen title="You" active="you" streak={streak} level={level} ad={AD_SLOTS.profile}>
      <div className="mc-in" style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 72, height: 72, flex: "0 0 auto", borderRadius: 24, display: "flex", alignItems: "center", justifyContent: "center", background: "radial-gradient(circle at 32% 28%, var(--accent-text), #65A30D 60%, #3F6212)", fontFamily: "'Space Grotesk',sans-serif", fontSize: 30, fontWeight: 700, color: "#fff", boxShadow: "0 8px 22px rgba(101,163,13,0.5)" }}>
            {name[0].toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 22, fontWeight: 700 }}>{name}</div>
            <div style={{ fontSize: 13, color: "var(--accent-text)" }}>Level {level} · {titleFor(level)}</div>
            <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: "var(--border)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.round(prog.pct * 100)}%`, background: "linear-gradient(90deg,#65A30D,#BEF264)", borderRadius: 999 }} />
            </div>
            <div style={{ marginTop: 5, fontSize: 11, color: "var(--text-3)" }}>{prog.into}/{prog.size} to level {level + 1}</div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 10 }}>
          <StatCard value={discovered} label="Discovered" />
          <StatCard value={streak} label="Day streak" color="#F5B740" />
          <StatCard value={`${Math.round((discovered / total) * 100)}%`} label="Codex complete" color="#34D399" />
          <StatCard value={earnedCount} label="Achievements" color="#F472B6" />
        </div>

        {/* Achievements */}
        <div>
          <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Achievements</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
            {achievements.map((a) => (
              <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 16, background: "var(--surface)", border: "1px solid var(--border)", opacity: a.unlocked ? 1 : 0.55 }}>
                <div style={{ width: 40, height: 40, flex: "0 0 auto", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, background: a.unlocked ? "rgba(101,163,13,0.16)" : "var(--fill)" }}>{a.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{a.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-2)" }}>{a.description}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, color: a.unlocked ? "#34D399" : "var(--text-3)", background: a.unlocked ? "rgba(52,211,153,0.14)" : "var(--fill)" }}>{a.unlocked ? "Earned" : "Locked"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <button className="mc-tap" onClick={onShare} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, minHeight: 50, border: "none", borderRadius: 14, background: "#65A30D", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", boxShadow: "0 8px 20px rgba(101,163,13,0.35)" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" /></svg>
          Share my progress
        </button>
        <button className="mc-tap" onClick={() => navigate("/settings")} style={{ display: "flex", alignItems: "center", gap: 12, minHeight: 52, padding: "0 16px", border: "1px solid var(--border)", borderRadius: 14, background: "var(--surface)", color: "var(--text)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-text)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
          <span style={{ flex: 1, textAlign: "left" }}>Settings &amp; Account</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>
    </Screen>
  );
};

export default ProfileScreen;
